import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Search, Download, Eye, EyeOff, ChevronRight, Check, ArrowLeft } from 'lucide-react';
import uploading from './../assets/uploading.png';
import download from './../assets/downloading.png';
import './Commponentmodal.css';
import { getVehicleClassData } from '../Utils/helper';
import { ErrorToast, SuccessToast } from '../Utils/toast';
import { API } from '../Common/Api';
import moment from 'moment';
import CommonTable from '../Common/CommonTable';
import { vehicleClasses } from '../Utils/Constants';
import CustomSelectInput from './SelectInput';
import { useDispatch, useSelector } from 'react-redux';
import { ACTIONS } from '../redux/actions';
import TextInput from './TextInput';
import Loading from '../Common/Loading';


// Fetch the full matching inventory in one request so we can group + paginate by batch on the client
const FETCH_ALL_LIMIT = 100000;

// Colored status pill — green for Assigned, red for Unassigned
const StatusBadge = ({ status }) => {
  const base = "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium";
  if (status === "Assigned")
    return <span className={`${base} bg-green-100 text-green-800`}><span className="h-2 w-2 bg-green-500 rounded-full" />Assigned</span>;
  if (status === "Unassigned")
    return <span className={`${base} bg-red-100 text-red-700`}><span className="h-2 w-2 bg-red-500 rounded-full" />Unassigned</span>;
  return <span className={`${base} bg-gray-100 text-gray-700`}>{status || 'N/A'}</span>;
};

// Custom-styled checkbox using the brand red accent (replaces default blue checkbox)
const CustomCheckbox = ({ checked = false, indeterminate = false, onChange, disabled = false, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <label className={`ct-checkbox ${disabled ? 'ct-checkbox--disabled' : ''} ${className}`}>
      <input ref={ref} type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      <span className="ct-checkbox__box">
        <Check className="ct-checkbox__tick" strokeWidth={3} />
      </span>
    </label>
  );
};

const AdminTableModal = ({
  open,
  onClose,
  title,
  distributorName,
  distributorId,
  requestId,
  mobile,
  utr,
  date,
  fileUrl,
  maxQuantity,
  reqVehicleClass,
  showSearch = true
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [vcClass, setVcClass] = useState(() => {
    if (reqVehicleClass) {
      const vData = getVehicleClassData(reqVehicleClass);
      return vData ? vData.value : reqVehicleClass;
    }
    return '';
  });
  const [activeTab, setActiveTab] = useState('available');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [assignCount, setAssignCount] = useState(0);
  const [selectedClass, setSelectedClass] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [visibleRows, setVisibleRows] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadCheckFlag, setUploadCheckFlag] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chips, setChips] = useState([]);
  const [inputValue, setInputValue] = useState('');

  // Batch -> Tag Ref ID inline expander (view-only)
  const [expandedBatchId, setExpandedBatchId] = useState(null);

  useEffect(() => {
    setSearchQuery(chips.join(','));
  }, [chips, setSearchQuery]);

  // Reset to first page whenever the search input changes (search is client-side)
  useEffect(() => {
    setCurrentPage(1);
  }, [chips, inputValue]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.includes(',') || value.includes(' ') || value.includes('\n')) {
      const tokens = value.split(/[\s,]+/).map(t => t.trim()).filter(Boolean);
      if (tokens.length > 0) {
        setChips(prev => {
          const uniqueTokens = tokens.filter(t => !prev.includes(t));
          return [...prev, ...uniqueTokens];
        });
        setInputValue('');
      }
    } else {
      setInputValue(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      const value = inputValue.trim().replace(/,$/, '');
      if (value) {
        const tokens = value.split(/[\s,]+/).map(t => t.trim()).filter(Boolean);
        setChips(prev => {
          const uniqueTokens = tokens.filter(t => !prev.includes(t));
          return [...prev, ...uniqueTokens];
        });
      }
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue) {
      setChips(prev => prev.slice(0, -1));
    }
  };

  const handleInputBlur = () => {
    const value = inputValue.trim();
    if (value) {
      const tokens = value.split(/[\s,]+/).map(t => t.trim()).filter(Boolean);
      setChips(prev => {
        const uniqueTokens = tokens.filter(t => !prev.includes(t));
        return [...prev, ...uniqueTokens];
      });
      setInputValue('');
    }
  };

  const removeChip = (indexToRemove) => {
    setChips(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const [approvalNote, setApprovalNote] = useState('');
  const [loading, setLoading] = useState(false);
  const selectedItemsMap = useRef({});

  const selectionConstraints = useRef({ vehicleClass: null });

  // Redux setup
  const dispatch = useDispatch();
  const assignDistributorExcelState = useSelector(state => state.AssignDistributorExcel || {});

  const getData = useCallback(async () => {
    setLoading(true);
    // Search is applied client-side (by whole batch), so we always pull the full VC dataset here
    const data = {
      page: 1,
      limit: FETCH_ALL_LIMIT,
      search: "",
      startDate: "",
      endDate: "",
      status: "Unassigned",
      // Activated FASTags cannot be moved — never show them in the assignable pool.
      isActivated: "false",
      VC: vcClass || ""
    };
    try {
      const response = await API.GetInventory(data);

      if (response.status === 200 && response.success) {
        setTableData(response.data || []);
        setPagination(response.pagination);
      } else {
        ErrorToast(response.message);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      ErrorToast("Failed to fetch inventory data.");
    } finally {
      setLoading(false);
    }
  }, [vcClass]);


  useEffect(() => {
    if (activeTab !== 'assignWithFile' && open) {
      if (activeTab === 'available') getData();
    }
  }, [activeTab, open, getData]);

  useEffect(() => {
    // Clear selection when tab or modal changes
    if (open) {
      setSelectedRows([]);
      selectedItemsMap.current = {};
      selectionConstraints.current = { vehicleClass: null };
      setExpandedBatchId(null);
    }
  }, [activeTab, open]);

  useEffect(() => {
    // Clear selection when vehicle class filter changes
    setSelectedRows([]);
    selectedItemsMap.current = {};
    selectionConstraints.current = { vehicleClass: null };
    setCurrentPage(1);
  }, [vcClass]);

  // Effect to handle Redux state changes for file upload
  useEffect(() => {
    if (uploadCheckFlag) {
      setIsUploading(assignDistributorExcelState.AssignDistributorExcelLoading);

      if (!assignDistributorExcelState.AssignDistributorExcelLoading) {
        if (assignDistributorExcelState.AssignDistributorExcelData) {
          SuccessToast('File uploaded and FASTags assigned successfully!');
          setSelectedFile(null);
          setUploadCheckFlag(false);
          // NOTE: Do NOT dispatch REFRESH_DATA here — the saga already dispatches it
          onClose(); // Close modal after successful file assignment
        } else if (assignDistributorExcelState.AssignDistributorExcelError) {
          const errMsg = assignDistributorExcelState.AssignDistributorExcelError;
          ErrorToast(typeof errMsg === 'string' ? errMsg : 'Failed to upload file');
          setUploadCheckFlag(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignDistributorExcelState, uploadCheckFlag]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleAssignNow = () => {
    assignNow();
  };

  const toggleVisibility = (index, row) => {
    const isVisible = visibleRows[index];
    const newVisibility = { ...visibleRows, [index]: !isVisible };
    setVisibleRows(newVisibility);
    if (window.innerWidth >= 0) {
      setModalOpen(!isVisible);
    }
    if (!isVisible) {
      setSelectedClass(row);
      setSelectedIndex(index);
    }
  };

  const vehicleData = selectedClass?.vehicleClass ? getVehicleClassData(selectedClass?.vehicleClass) : null;

  const fileInputRef = useRef(null);
  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  const validateFile = (file) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      '.xlsx',
      '.xls'
    ];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.some(type => file.type === type || file.name.toLowerCase().endsWith(type))) {
      ErrorToast('Please select a valid Excel file (.xlsx or .xls)');
      return false;
    }
    if (file.size > maxSize) {
      ErrorToast('File size should not exceed 5MB');
      return false;
    }
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
    } else {
      e.target.value = '';
    }
  };

  const handleFileUpload = () => {
    if (!selectedFile) {
      ErrorToast('Please select a file first');
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('distributorId', distributorId);
    if (requestId) {
      formData.append('requestId', requestId);
    }
    dispatch(ACTIONS.AssignDistributorExcelAction(formData));
    setUploadCheckFlag(true);
  };

  const handleDownloadSample = async (e) => {
    e.preventDefault();
    try {
      const params = { distributorId };
      const blob = await API.GetAdminExcelInventory(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'sample_admin_inventory.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      SuccessToast('Sample file downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      ErrorToast('Failed to download sample file. Please try again.');
    }
  };

  // Group rows by unique Batch ID (UI-only grouping, no change to API calls)
  // Show each FASTag as its own row (no Batch ID grouping). Selection / search /
  // pagination logic all operate on these single-item "groups".
  const groupByBatch = (rows) => (rows || []).map((item) => ({
    batchId: item.batchId || 'N/A',
    tagRefId: item.tagRefId,
    items: [item],
    sample: item,
  }));

  const groupedAvailable = useMemo(() => groupByBatch(tableData), [tableData]);

  // Search tokens (committed chips + the value being typed)
  const searchTokens = useMemo(() => {
    const toks = [...chips];
    if (inputValue.trim()) toks.push(inputValue.trim());
    return toks.map((t) => t.toLowerCase());
  }, [chips, inputValue]);

  // A batch matches if ANY of its tags matches ANY token (by tag ref / serial / batch id).
  // Matching keeps the WHOLE batch so selection stays in full bunches.
  const filteredGrouped = useMemo(() => {
    if (searchTokens.length === 0) return groupedAvailable;
    return groupedAvailable.filter((g) =>
      g.items.some((item) =>
        searchTokens.some((tok) =>
          (item.tagRefId || '').toLowerCase().includes(tok) ||
          (item.serialNumber || '').toLowerCase().includes(tok) ||
          (item.batchId || '').toLowerCase().includes(tok)
        )
      )
    );
  }, [groupedAvailable, searchTokens]);

  // Client-side pagination over batches
  const paginatedAvailable = useMemo(
    () => filteredGrouped.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredGrouped, currentPage, itemsPerPage]
  );

  // Tags shown on the current page (used for the "select all" header checkbox)
  const currentPageItems = useMemo(
    () => paginatedAvailable.flatMap((g) => g.items),
    [paginatedAvailable]
  );
  const isAllSelected = currentPageItems.length > 0 && currentPageItems.every((it) => selectedRows.includes(it._id));

  // Add ids to the selection while respecting the maxQuantity limit
  const addIdsRespectingLimit = (idsToAdd, sourceItems) => {
    const limit = Number(maxQuantity);
    setSelectedRows((prev) => {
      const prevSet = new Set(prev);
      const newIds = idsToAdd.filter((id) => !prevSet.has(id));
      if (newIds.length === 0) return prev;

      let toAdd = newIds;
      if (limit && prevSet.size + newIds.length > limit) {
        const space = limit - prevSet.size;
        if (space <= 0) {
          ErrorToast(`You cannot select more than ${limit} FASTags.`);
          return prev;
        }
        toAdd = newIds.slice(0, space);
        SuccessToast(`Only ${space} more items could be selected to reach the limit of ${limit}.`);
      }

      const toAddSet = new Set(toAdd);
      sourceItems.forEach((it) => {
        if (toAddSet.has(it._id)) selectedItemsMap.current[it._id] = it.vehicleClass;
      });
      return [...prev, ...toAdd];
    });
  };

  // Selection state of a batch row: 'all' | 'some' | 'none'
  const getBatchSelectionState = (group) => {
    const ids = group.items.map((i) => i._id);
    const selectedCount = ids.filter((id) => selectedRows.includes(id)).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === ids.length) return 'all';
    return 'some';
  };

  const removeIds = (idsToRemove) => {
    setSelectedRows((prev) => {
      const s = new Set(idsToRemove);
      const remaining = prev.filter((id) => !s.has(id));
      if (remaining.length === 0) selectionConstraints.current = { vehicleClass: null };
      return remaining;
    });
  };

  const toggleBatchSelection = (group, checked) => {
    const ids = group.items.map((i) => i._id);
    if (checked) {
      const batchVC = group.sample?.vehicleClass;
      const currentVC = selectionConstraints.current.vehicleClass;
      if (selectedRows.length > 0 && currentVC && currentVC !== batchVC) {
        ErrorToast(`You can only select FASTags of the same Vehicle Class (${currentVC})`);
        return;
      }
      if (selectedRows.length === 0 || !currentVC) {
        selectionConstraints.current = { vehicleClass: batchVC };
      }
      addIdsRespectingLimit(ids, group.items);
    } else {
      removeIds(ids);
    }
  };

  // Header "select all" — selects matching-VC tags on the current page, respecting the limit
  const handleSelectAllChange = (e) => {
    if (e.target.checked) {
      let targetVC = selectionConstraints.current.vehicleClass;
      if (!targetVC && currentPageItems.length > 0) {
        targetVC = currentPageItems[0].vehicleClass;
        selectionConstraints.current = { vehicleClass: targetVC };
      }
      const matching = currentPageItems.filter(
        (r) => !selectedRows.includes(r._id) && (!targetVC || r.vehicleClass === targetVC)
      );
      addIdsRespectingLimit(matching.map((r) => r._id), matching);
    } else {
      setSelectedRows((prev) => {
        const ids = new Set(currentPageItems.map((r) => r._id));
        const remaining = prev.filter((id) => !ids.has(id));
        if (remaining.length === 0) selectionConstraints.current = { vehicleClass: null };
        return remaining;
      });
    }
  };

  const toggleBatchExpand = (batchId) => {
    setExpandedBatchId((prev) => (prev === batchId ? null : batchId));
  };

  const BatchIdCell = ({ group }) => {
    const isExpanded = group.batchId === expandedBatchId;
    return (
      <button
        type="button"
        onClick={() => toggleBatchExpand(group.batchId)}
        className="group flex items-center gap-1 font-medium text-[#E2231A] hover:underline"
        title="View Tag Ref IDs"
      >
        <span>{group.batchId}</span>
        <span className="text-[10px] bg-[#FEECEC] text-[#E2231A] rounded-full px-1.5 py-0.5 font-semibold">
          {group.items.length}
        </span>
        <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : 'group-hover:translate-x-0.5'}`} />
      </button>
    );
  };

  // Plain Tag Ref ID cell (flat list — no batch grouping / expand).
  const TagRefCell = ({ group }) => (
    <span className="font-medium text-gray-800 break-all">{group.sample?.tagRefId || 'N/A'}</span>
  );

  const VehicleClassCell = ({ group, idx }) => {
    const vehicleClassData = getVehicleClassData(group.sample?.vehicleClass);
    return (
      <div className="flex gap-2 items-center">
        <img src={vehicleClassData?.tagimage} alt="Tag" className="w-[34px]" />
        <p>{vehicleClassData?.value || group.sample?.vehicleClass}</p>
        <div className="pl-2 flex items-center cursor-pointer" onClick={() => toggleVisibility(idx, group.sample)}>
          {visibleRows[idx] ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
        </div>
      </div>
    );
  };

  // Inline view-only list of Tag Ref IDs for an expanded batch row
  const renderBatchTags = (group) => {
    if (group.batchId !== expandedBatchId) return null;
    return (
      <div className="bg-[#FAFAFA] px-4 py-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Tag Ref IDs ({group.items.length})
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {group.items.map((item) => (
            <label
              key={item._id}
              className="p-2.5 rounded-lg border border-gray-200 bg-white flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                checked={selectedRows.includes(item._id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    addIdsRespectingLimit([item._id], group.items);
                  } else {
                    removeIds([item._id]);
                  }
                }}
              />
              <p className="font-semibold text-sm text-gray-800 break-all">{item.tagRefId}</p>
            </label>
          ))}
        </div>
      </div>
    );
  };

  // Dedicated to the download button: the screen's other loading flags drive
  // the table overlay and a refetch, which a download must not trigger.
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadExcel = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const data = {
        search: searchQuery || "",
        startDate: "",
        endDate: "",
        status: "Unassigned",
        VC: vcClass || "",
        distributorId: distributorId
      };

      const blob = await API.ExportAdminAssignInventory(data);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AdminInventory_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      SuccessToast('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Excel Download failed:', error);
      if (error.message === "No inventory records found") {
        ErrorToast('No inventory records found for the given filters.');
      } else {
        ErrorToast('Failed to download Excel file. Please try again.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const assignNow = async () => {
    let params = {
      FasTagIds: [requestId],
      inventoryIds: selectedRows,
      remark: approvalNote || "Assigned by Admin"
    }

    setShowConfirmModal(false);
    setLoading(true);
    try {
      // Safety timeout: never let the UI freeze if the request hangs (e.g. a slow
      // external portal). Whichever settles first wins.
      const response = await Promise.race([
        API.ApproveFastag(JSON.stringify(params)),
        new Promise((_, reject) => setTimeout(() => reject(new Error('__timeout__')), 90000)),
      ]);

      if (response.status === 200 && response.success) {
        setShowConfirmModal(false);
        SuccessToast(response.message);
        dispatch({ type: "REFRESH_DATA" });
        setAssignCount(0);
        setSelectedRows([]);
        selectedItemsMap.current = {};
        getData();
        onClose();
      } else {
        setShowConfirmModal(false);
        ErrorToast(response.message || 'Failed to approve FASTag request.');
      }
    } catch (error) {
      console.error("Error approving FASTag:", error);
      setShowConfirmModal(false);
      ErrorToast(error?.message === '__timeout__'
        ? 'The request is taking too long. Please try again.'
        : 'Failed to approve FASTag request.');
    } finally {
      setLoading(false);
    }
  };


  const tableColumns = [
    {
      label: (
        <CustomCheckbox checked={isAllSelected} onChange={handleSelectAllChange} />
      ),
      key: "select",
      tdClassName: "text-center",
      render: (group) => {
        const state = getBatchSelectionState(group);
        return (
          <CustomCheckbox
            checked={state === 'all'}
            indeterminate={state === 'some'}
            onChange={(e) => toggleBatchSelection(group, e.target.checked)}
          />
        );
      },
    },
    {
      label: "Tag Ref ID",
      key: "tagRefId",
      mobileMain: true,
      render: (group) => <TagRefCell group={group} />,
    },
    {
      label: "Batch Id",
      key: "batchId",
      render: (group) => <span className="text-gray-700 break-all">{group.sample?.batchId || 'N/A'}</span>,
    },
    {
      label: "Vehicle Class",
      key: "value",
      render: (group, idx) => <VehicleClassCell group={group} idx={idx} />,
    },
    { label: "Status", render: (group) => <StatusBadge status={group.sample?.status} /> },
    {
      label: "Purchase Date",
      render: (group) => moment(group.sample?.createdAt).format("DD MMMM, YYYY, hh:mm A"),
      tdClassName: "text-end"
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      <div className="bg-white w-full h-full overflow-hidden flex flex-col relative">


        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelectedRows([]); selectedItemsMap.current = {}; onClose(); }}
              className="text-gray-600 hover:text-red-500 p-1"
              title="Back"
            >
              <ArrowLeft size={22} />
            </button>
            <div className="px-2 py-3 text-sm font-medium text-gray-800 ">
              <span className="font-semibold"> {title}</span>
            </div>
          </div>
          <button onClick={() => { setSelectedRows([]); selectedItemsMap.current = {}; onClose(); }}
            className="text-gray-500 hover:text-gray-700">
            <X size={22} />
          </button>
        </div>

        {/* Optional Details Section */}
        {(mobile || utr || date || fileUrl) && (
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 px-6 py-4 bg-gray-50 border-b text-xs shrink-0">
            {mobile && <div><span className="font-medium text-gray-500">Mobile:</span> <span className="font-semibold">{mobile}</span></div>}
            {reqVehicleClass && <div><span className="font-medium text-gray-500">Vehicle Class:</span> <span className="font-semibold">{reqVehicleClass}</span></div>}
            {maxQuantity && <div><span className="font-medium text-gray-500">Quantity:</span> <span className="font-bold text-red-600">{maxQuantity}</span></div>}
            {utr && <div><span className="font-medium text-gray-500">UTR:</span> <span className="font-semibold text-gray-700">{utr}</span></div>}
            {date && <div><span className="font-medium text-gray-500">Date:</span> <span className="font-semibold">{date}</span></div>}
            {fileUrl && (
              <div>
                <span className="font-medium text-gray-500">Screenshot:</span>
                <button
                  onClick={() => {
                    if (fileUrl) {
                      API.DownloadReceipt(fileUrl).catch(err => {
                        console.error('Download failed:', err);
                        ErrorToast('Failed to download receipt');
                      });
                    }
                  }}
                  className='ml-2 text-blue-500 hover:text-blue-700 hover:underline cursor-pointer bg-transparent border-none p-0'
                >
                  Download Receipt
                </button>
              </div>
            )}
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex flex-col flex-1 overflow-hidden relative">
          {/* Tabs & Filters */}
          {showConfirmModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg p-6 w-[400px] shadow-xl text-center">
                <p className="text-gray-800 text-lg font-medium mb-2">
                  You’re assigning {assignCount} FASTags to {distributorName}.
                </p>
                <p className="text-sm font-bold text-gray-800 mb-6">Are you sure?</p>
                <div className="flex justify-center gap-4">
                  <button onClick={() => setShowConfirmModal(false)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-full text-sm">Cancel</button>
                  <button onClick={handleAssignNow} className="bg-[#07AE4D] hover:bg-green-600 text-white px-6 py-2 rounded-full text-sm">Submit</button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {activeTab === 'available' && (
            <CommonTable
              isPagination={true}
              loading={loading}
              hideDate={true}

              paginationClass="!mt-0 !justify-between "
              columns={tableColumns}
              leftComponent={
                <div className="flex items-center space-x-2 ">
                  <button onClick={() => setActiveTab('available')} className={`${activeTab === 'available' ? 'bg-red-500 text-white' : 'bg-[#FEF5F5] text-[#1F1715]'} text-[12px] font-medium px-4 py-2 rounded-full`}>Available FASTag</button>
                  <button onClick={() => setActiveTab('assignWithFile')} className={`${activeTab === 'assignWithFile' ? 'bg-red-500 text-white' : 'bg-[#FEF5F5] text-[#1F1715]'} text-[12px] font-medium px-4 py-2 rounded-full`}>Assign with file</button>
                </div>
              }
              rightComponent={
                <div className="flex items-center gap-1.5">
                  {selectedRows.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[12px] font-bold ${selectedRows.length === Number(maxQuantity) ? 'text-[#07AE4D]' : 'text-gray-500 underline decoration-dotted'}`}>
                        Selected: {selectedRows.length} / {maxQuantity}
                      </span>
                      <button
                        className="bg-[#07AE4D] text-white text-[12px] px-4 py-2 rounded-full"
                        onClick={() => {
                          const uniqueSelectedIds = [...new Set(selectedRows)];
                          const limit = Number(maxQuantity);

                          if (limit && uniqueSelectedIds.length !== limit) {
                            if (uniqueSelectedIds.length < limit) {
                              ErrorToast(`Incomplete selection. Please select exactly ${limit} FASTags. You currently have ${uniqueSelectedIds.length}.`);
                            } else {
                              ErrorToast(`Maximum limit exceeded. You selected ${uniqueSelectedIds.length} items, but can only assign ${limit}.`);
                            }
                            return;
                          }

                          if (reqVehicleClass) {
                            const invalidSelection = selectedRows.some(id => selectedItemsMap.current[id] && selectedItemsMap.current[id] !== reqVehicleClass);
                            if (invalidSelection) {
                              const vData = getVehicleClassData(reqVehicleClass);
                              ErrorToast(`Invalid vehicle class. Please select only ${vData?.value || reqVehicleClass} tags.`);
                              return;
                            }
                          }
                          setAssignCount(selectedRows.length);
                          setShowConfirmModal(true);
                        }}
                      >
                        Assign Now
                      </button>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5">
                    {showSearch && (
                      <div className="flex flex-wrap items-center gap-1 p-1 px-2 border border-gray-300 rounded-md focus-within:ring-1 focus-within:ring-red-500 w-[320px] bg-white min-h-[32px] max-h-[120px] overflow-y-auto relative">
                        {chips.map((chip, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 text-gray-800 text-[10px] px-2 py-0.5 rounded-full border border-gray-200 shrink-0">
                            <span>{chip}</span>
                            <button
                              type="button"
                              onClick={() => removeChip(idx)}
                              className="text-gray-400 hover:text-red-500 focus:outline-none"
                            >
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                        <input
                          type="text"
                          placeholder={chips.length === 0 ? "Search Batch ID / Tag ID (space/comma)..." : ""}
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          onBlur={handleInputBlur}
                          className="flex-1 min-w-[120px] border-none outline-none focus:ring-0 p-0.5 text-[11px] text-gray-700 bg-transparent"
                        />
                        {chips.length === 0 && !inputValue && (
                          <Search className="text-gray-400 w-3.5 h-3.5 mr-1 select-none pointer-events-none shrink-0" />
                        )}
                      </div>
                    )}
                    <CustomSelectInput name="value" value={vcClass} onChange={(e) => setVcClass(e.target.value)} options={vehicleClasses.map((item) => ({ ...item, name: item.value, value: item.value }))} className="w-[130px] shrink-0" inputClass="!mt-0 py-1 px-2 text-xs h-[32px]" labelClass="hidden" />
                  </div>
                </div>
              }
              data={paginatedAvailable}
              emptyMessage="No Inventory Found"
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              selectedDate={selectedDate}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
              setSelectedDate={setSelectedDate}
              onDownload={handleDownloadExcel}
              downloading={isDownloading}
              total={filteredGrouped.length}
              totalPages={Math.ceil(filteredGrouped.length / itemsPerPage)}
              extraTableClass="flex-1 overflow-y-auto [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10"
              BottomComponent={
                <div className="p-4 bg-gray-50 border-t shrink-0">
                  <label className="block text-sm font-bold text-gray-800 mb-2">Approval Note</label>
                  <textarea
                    placeholder="Enter remarks (optional)"
                    value={approvalNote}
                    onChange={(e) => setApprovalNote(e.target.value)}
                    className="w-full px-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows="2"
                  />
                </div>
              }
            />
          )}

          {activeTab === 'assignWithFile' && (
            <div className="flex flex-col h-full overflow-hidden relative">
              <Loading loading={loading} overlay={true} />

              <div className="flex items-center m-2 shrink-0">
                <div className="flex items-center space-x-2 ">
                  <button onClick={() => setActiveTab('available')} className={`${activeTab === 'available' ? 'bg-red-500 text-white' : 'bg-[#FEF5F5] text-[#1F1715]'} text-[12px] font-medium px-4 py-2 rounded-full`}>Available FASTag</button>
                  <button onClick={() => setActiveTab('assignWithFile')} className={`${activeTab === 'assignWithFile' ? 'bg-red-500 text-white' : 'bg-[#FEF5F5] text-[#1F1715]'} text-[12px] font-medium px-4 py-2 rounded-full`}>Assign with file</button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col md:flex-row justify-center gap-8 px-6 py-[51px] border-t">
                  <div className="flex flex-col items-center space-y-4 w-full md:w-auto">
                    <div onClick={!isUploading ? handleBoxClick : undefined} className={`border-2 ${selectedFile ? 'border-blue-500' : 'border-green-500'} flex justify-center items-center gap-[18px] rounded-lg p-6 text-center ${selectedFile ? 'text-blue-700' : 'text-green-700'} ${!isUploading ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} text-sm w-full md:w-[306px]`}>
                      {selectedFile ? (
                        <div className="flex flex-col items-center"><span className="font-medium mb-1">Selected File:</span><span className="text-xs text-gray-600 break-all">{selectedFile.name}</span><span className="text-xs text-gray-500">({(selectedFile.size / 1024).toFixed(2)} KB)</span></div>
                      ) : (
                        <>Select / Drop File here<span className="ml-2 text-lg"><img src={uploading} className="w-[18px]" alt="Upload Icon" /></span></>
                      )}
                    </div>
                    {selectedFile && (
                      <div className="flex gap-2">
                        <button onClick={handleFileUpload} disabled={isUploading} className={`px-6 py-2 rounded-full text-sm font-medium ${isUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#07AE4D] hover:bg-green-600'} text-white`}>{isUploading ? 'Uploading...' : 'Upload & Assign'}</button>
                        <button onClick={() => { setSelectedFile(null); fileInputRef.current.value = ''; }} disabled={isUploading} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-300 hover:bg-gray-400 text-gray-700">Clear</button>
                      </div>
                    )}
                    {isUploading && <div className="flex items-center gap-2 text-sm text-gray-600"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>Processing file...</div>}
                  </div>
                  <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept=".xlsx,.xls" />
                  <div className="hidden md:block w-px bg-[#9C9B9B]" />
                  <div className="text-sm text-gray-700 flex flex-col justify-center">
                    <a
                      href="/samples/Sample Inventory Assigned Distrubuter.xlsx"
                      download="Sample Inventory Assigned Distrubuter.xlsx"
                      className="text-blue-600 font-medium flex items-center gap-1 mb-2 cursor-pointer no-underline hover:underline"
                    >
                      <span><img src={download} className="w-[13px]" alt="Download Icon" /></span>
                      Download sample file
                    </a>
                    <ul className="list-disc pl-4 space-y-1 text-gray-600">
                      <li>Download sample file</li>
                      <li>Fill all required columns</li>
                      <li>Upload and check file status</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="relative bg-white rounded-[20px] shadow-lg w-full max-w-xl p-6">
            <div className="absolute top-4 right-4 gap-3 flex-row flex">
              <button
                onClick={() => {
                  setModalOpen(false);
                  if (selectedIndex !== null) {
                    toggleVisibility(selectedIndex, selectedClass);
                  }
                }}
                className="text-gray-600 hover:text-red-500"
              >
                <X size={20} />
              </button>
            </div>
            <h2 className="text-xl font-semibold mb-[4px] text-center">
              {vehicleData?.value}
            </h2>
            <p className="mb-[30px] text-sm text-center">
              {vehicleData?.description}
            </p>
            <div className="relative w-full flex justify-center items-center py-8 px-4">
              <div className="relative bg-white p-8 rounded-2xl shadow-2xl border border-gray-200 max-w-2xl w-full">
                {/* Decorative corner accents */}
                <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-red-500 rounded-tl-2xl opacity-20"></div>
                <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-red-500 rounded-br-2xl opacity-20"></div>

                {/* Image container with classic frame */}
                <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border-2 border-gray-300 shadow-inner">
                  <img
                    src={vehicleData?.cardWebp}
                    alt={vehicleData?.name}
                    className="w-full h-auto object-contain rounded-lg transition-transform duration-500 hover:scale-[1.12]"
                  />
                </div>

                {/* Elegant bottom accent */}
                <div className="mt-6 flex justify-center">
                  <div className="h-1 w-24 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTableModal;
